import { without, mapValues, pick } from 'lodash-es';
import { getGlobalSettings } from './global_settings';

const { availableCohorts, availableCohortsSegments, advancedMetrics, advancedMetricsV2 } =
  getGlobalSettings();

// ADVANCED_METRICS_APP: short titles keyed by metric (used in app-insights UI)
const APP_METRICS = [
  'mau_penetration',
  'wau_penetration',
  'sessions_penetration',
  'time_spent_penetration',
  'time_spent_segment_penetration',
  'average_time_spent_per_dau',
  'average_sessions_per_dau',
  'churn',
];
export const ADVANCED_METRICS_APP = mapValues(pick(advancedMetrics, APP_METRICS), (m) => m.title);

// ADVANCED_METRICS_APP_DESCRIPTION: descriptions keyed by metric (used in tooltips)
export const ADVANCED_METRICS_APP_DESCRIPTION = mapValues(
  pick(advancedMetrics, APP_METRICS),
  (m) => m.description
);

// ADVANCED_METRICS: full titles keyed by metric (used in company page / transforms)
export const ADVANCED_METRICS = mapValues(advancedMetrics, (m) => m.full_title);

// ADVANCED_METRICS_V2: full titles keyed by metric (used in export modal / custom exports)
export const ADVANCED_METRICS_V2 = mapValues(advancedMetricsV2, (m) => m.full_title);

export const ADVANCED_METRICS_PERCENTAGE = [
  'mau_penetration',
  'wau_penetration',
  'churn',
  'sessions_penetration',
  'time_spent_penetration',
  'time_spent_segment_penetration',
];

export const PANEL_VERSIONS = {
  'United States': 'us-blended-v2-r1-0-public',
  'United States-private': 'us-blended-v2-r1-0',
  APAC: 'bobble-v2-r1-0-apac-public',
  'APAC-private': 'bobble-v2-r1-0-apac',
  Europe: 'ipsos-v1-r1-0-public',
  'Europe-private': 'ipsos-v1-r1-0',
};

export const PUBLIC_PANEL_VERSIONS = {
  US: 'us-blended-v2-r1-0-public',
  APAC: 'bobble-v2-r1-0-apac-public',
  Europe: 'ipsos-v1-r1-0-public',
};

export const PANEL_ALLOWED_COUNTRIES = {
  US: ['US'],
  APAC: ['APAC'],
  Europe: ['GB'],
  'us-blended-v2-r1-0-public': ['US'],
  'us-blended-v2-r1-0': ['US'],
  'bobble-v2-r1-0-apac-public': ['APAC'],
  'bobble-v2-r1-0-apac': ['APAC'],
  'ipsos-v1-r1-0-public': ['GB'],
  'ipsos-v1-r1-0': ['GB'],
};

export const EXISTING_CROSS_APP_METRICS_PANELS = [
  'us-blended-v2-r1-0-public',
  'us-blended-v2-r1-0',
];
export const CROSS_APP_PANEL_VERSION_MAPPING = {
  'us-blended-v2-r1-0-public': 'mfour-v2-r3-0-public',
  'us-blended-v2-r1-0': 'mfour-v2-r3-0',
};

const metricsV2Public = without(
  Object.keys(ADVANCED_METRICS_V2),
  'dau_penetration',
  'cross_app_wau_penetration'
);
const metricsV2Private = without(Object.keys(ADVANCED_METRICS_V2), 'dau_penetration');

export const ADVANCED_METRICS_V2_MAP = {
  [PANEL_VERSIONS['United States']]: metricsV2Public,
  [PANEL_VERSIONS['United States-private']]: metricsV2Private,
  [PANEL_VERSIONS.APAC]: metricsV2Public,
  [PANEL_VERSIONS['APAC-private']]: metricsV2Private,
  [PANEL_VERSIONS.Europe]: metricsV2Public,
  [PANEL_VERSIONS['Europe-private']]: metricsV2Private,
};

export const COHORTS = availableCohorts || {};
export const COHORTS_SEGMENTS = availableCohortsSegments || {};
export const AI_COHORTS_TOOLTIPS = {
  never_ai_users:
    'Panelists who have never had a session longer than 60 seconds in any AI app in scope.',
  churned_ai_users:
    'Panelists who used an AI app more than 30 days ago but have not used any AI app in the past 30 days.',
  low_frequency_ai_users: 'Panelists whose last AI app usage was 8–30 days ago.',
  medium_frequency_ai_users: 'Panelists who used an AI app 1–2 days in the past 7 days (rolling).',
  high_frequency_ai_users: 'Panelists who used an AI app 3–7 days in the past 7 days (rolling).',
};

const cohorts = without(Object.keys(COHORTS), 'younger_users');

export const AI_COHORTS_SHORT_NAMES = {
  never_ai_users: 'Never AI',
  churned_ai_users: 'Churned AI',
  low_frequency_ai_users: 'Low Freq. AI',
  medium_frequency_ai_users: 'Med. Freq. AI',
  high_frequency_ai_users: 'High Freq. AI',
};

export const COHORTS_BY_PANEL = Object.values(PANEL_VERSIONS).reduce((acc, panelVersion) => {
  acc[panelVersion] = cohorts;
  return acc;
}, {});

const groupedCohorts = mapValues(COHORTS_SEGMENTS, (segments, key) => {
  if (key === 'general_segments') return without(segments, 'younger_users');
  return segments;
});

export const GROUPED_COHORTS_BY_PANEL = Object.values(PANEL_VERSIONS).reduce(
  (acc, panelVersion) => {
    acc[panelVersion] = groupedCohorts;
    return acc;
  },
  {}
);

export const PANELS_REGIONS = [
  {
    label: 'North America',
    value: 'NORTHAM',
    list: ['US', 'CA'],
    panel_iso: 'US',
    panel_label: 'United States',
    panel_version: 'us-blended-v2-r1-0-public',
  },
  {
    label: 'APAC',
    value: 'APAC',
    list: [
      'AU',
      'BD',
      'BN',
      'BT',
      'CN',
      'FJ',
      'FM',
      'HK',
      'ID',
      'IN',
      'JP',
      'KH',
      'KR',
      'LA',
      'LK',
      'MN',
      'MO',
      'MY',
      'NP',
      'NZ',
      'PG',
      'PH',
      'PK',
      'PW',
      'SB',
      'SG',
      'TH',
      'TW',
      'VN',
    ],
    panel_iso: 'APAC',
    panel_label: 'APAC',
    panel_version: 'bobble-v2-r1-0-apac-public',
  },
  {
    label: 'Europe',
    value: 'europe',
    list: [
      'AD',
      'AL',
      'AT',
      'BA',
      'BE',
      'BG',
      'BY',
      'CH',
      'CY',
      'CZ',
      'DE',
      'DK',
      'EE',
      'ES',
      'FI',
      'FR',
      'GB',
      'GR',
      'HR',
      'HU',
      'IE',
      'IS',
      'IT',
      'LI',
      'LT',
      'LU',
      'LV',
      'MC',
      'MD',
      'ME',
      'MK',
      'MT',
      'NL',
      'NO',
      'PL',
      'PT',
      'RO',
      'RS',
      'RU',
      'SE',
      'SI',
      'SK',
      'UA',
    ],
    panel_iso: 'GB',
    panel_label: 'Europe',
    panel_version: 'ipsos-v1-r1-0-public',
  },
];

export const getPanelRegion = (countriesIso, availableDatasets) => {
  const panelIsoToDataset = {
    US: 'US',
    APAC: 'APAC',
    GB: 'Europe',
  };

  const panelRegionsToRegions = {
    NORTHAM: 'NORTHAM',
    APAC: 'APAC',
    europe: 'EMEA',
  };

  const panelRegions = availableDatasets
    ? PANELS_REGIONS.filter(({ panel_iso }) =>
        availableDatasets.includes(panelIsoToDataset[panel_iso] || panel_iso)
      )
    : PANELS_REGIONS;

  const panelRegion = panelRegions.find(({ value, list }) => {
    if (countriesIso.includes(panelRegionsToRegions[value] || value)) return true;

    const unionSize = new Set([...countriesIso, ...list]).size;

    return unionSize < countriesIso.length + list.length;
  });

  return panelRegion || null;
};
